import React, { useState } from 'react';
import { X, Settings } from 'lucide-react';
import { Sheet } from './ui/sheet';
import { Button } from './ui/button';

interface SettingsPanelProps {
  open: boolean;
  onClose: () => void;
  sessionId: string;
  currentEmail: string | null;
  theme?: 'light' | 'dark';
  onThemeChanged?: (theme: 'light' | 'dark') => void;
  editorFontSize?: number;
  editorTheme?: 'vs' | 'vs-dark' | 'hc-black';
  formatOnSave?: boolean;
  onEditorPrefsChanged?: (prefs: {
    fontSize?: number;
    theme?: 'vs' | 'vs-dark' | 'hc-black';
    formatOnSave?: boolean;
  }) => void;
  onEmailUpdated?: (email: string) => void;
  onLoggedOut?: () => void;
}

const SettingsPanel: React.FC<SettingsPanelProps> = ({
  open,
  onClose,
  sessionId,
  currentEmail,
  theme = 'light',
  onThemeChanged,
  editorFontSize = 14,
  editorTheme = 'vs-dark',
  formatOnSave = true,
  onEditorPrefsChanged,
  onEmailUpdated,
  onLoggedOut,
}) => {
  const [email, setEmail] = useState<string>(currentEmail || '');
  const [password, setPassword] = useState<string>('');
  const [confirmPassword, setConfirmPassword] = useState<string>('');
  const [status, setStatus] = useState<string>('');
  const [working, setWorking] = useState<boolean>(false);
  const [themeLocal, setThemeLocal] = useState<'light' | 'dark'>(theme);
  const [editorFont, setEditorFont] = useState<number>(editorFontSize);
  const [editorThemeLocal, setEditorThemeLocal] = useState<
    'vs' | 'vs-dark' | 'hc-black'
  >(editorTheme);
  const [formatOnSaveLocal, setFormatOnSaveLocal] =
    useState<boolean>(formatOnSave);

  const updateEmail = async () => {
    setStatus('');
    if (!email || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
      setStatus('Please enter a valid email.');
      return;
    }
    try {
      setWorking(true);
      const res = await fetch('/api/auth/update-email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: 'Bearer ' + sessionId,
        },
        body: JSON.stringify({ email }),
      });
      let data: any = {};
      try {
        if (
          (res.headers.get('content-type') || '').includes('application/json')
        ) {
          data = await res.json();
        } else {
          await res.text();
        }
      } catch {}
      if (!res.ok) throw new Error(data?.error || 'Failed to update email');
      localStorage.setItem('userEmail', data.email);
      onEmailUpdated && onEmailUpdated(data.email);
      setStatus('Email updated.');
    } catch (err: any) {
      setStatus('Failed to update email');
    } finally {
      setWorking(false);
    }
  };

  const updatePassword = async () => {
    setStatus('');
    if (!password || password.length < 6) {
      setStatus('Password must be at least 6 characters.');
      return;
    }
    if (password !== confirmPassword) {
      setStatus('Passwords do not match.');
      return;
    }
    try {
      setWorking(true);
      const res = await fetch('/api/auth/update-password', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: 'Bearer ' + sessionId,
        },
        body: JSON.stringify({ password }),
      });
      let data: any = {};
      try {
        if (
          (res.headers.get('content-type') || '').includes('application/json')
        ) {
          data = await res.json();
        } else {
          await res.text();
        }
      } catch {}
      if (!res.ok) throw new Error(data?.error || 'Failed to update password');
      setStatus('Password updated.');
      setPassword('');
      setConfirmPassword('');
    } catch (err: any) {
      setStatus('Failed to update password');
    } finally {
      setWorking(false);
    }
  };

  const updateTheme = async (next: 'light' | 'dark') => {
    setStatus('');
    // Optimistic local apply
    setThemeLocal(next);
    onThemeChanged && onThemeChanged(next);
    try {
      setWorking(true);
      const res = await fetch('/api/auth/update-theme', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: 'Bearer ' + sessionId,
        },
        body: JSON.stringify({ theme: next }),
      });
      let data: any = {};
      try {
        if (
          (res.headers.get('content-type') || '').includes('application/json')
        ) {
          data = await res.json();
        } else {
          await res.text();
        }
      } catch {}
      if (!res.ok) throw new Error(data?.error || 'Failed to update theme');
      // Success: no banner/status for theme changes
    } catch (err: any) {
      // Keep local change, inform user with a friendly message only
      setStatus('Failed to save theme on server — applied locally.');
    } finally {
      setWorking(false);
    }
  };

  const logout = async () => {
    try {
      setWorking(true);
      await fetch('/api/auth/logout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sessionId }),
      });
    } catch {
      // ignore network errors on logout
    } finally {
      setWorking(false);
      onLoggedOut && onLoggedOut();
      onClose();
    }
  };

  return (
    <Sheet open={open} onOpenChange={o => !o && onClose()} side='right'>
      <div className='flex items-center justify-between border-b px-4 py-3'>
        <div className='flex items-center gap-2'>
          <Settings size={16} />
          <h3 className='m-0 text-base font-medium'>Settings</h3>
        </div>
        <button
          onClick={onClose}
          className='text-muted-foreground hover:text-foreground'
        >
          <X size={16} />
        </button>
      </div>
      <div className='p-4 space-y-4 overflow-y-auto h-[calc(100%-48px)]'>
        {status && (
          <div className='rounded-md bg-accent text-accent-foreground px-3 py-2 text-sm'>
            {status}
          </div>
        )}

        <section className='space-y-2'>
          <h4 className='text-sm font-medium'>Appearance</h4>
          <div className='flex items-center gap-2'>
            <Button
              variant={themeLocal === 'light' ? 'default' : 'secondary'}
              onClick={() => updateTheme('light')}
              disabled={working}
            >
              Light
            </Button>
            <Button
              variant={themeLocal === 'dark' ? 'default' : 'secondary'}
              onClick={() => updateTheme('dark')}
              disabled={working}
            >
              Dark
            </Button>
          </div>
        </section>

        <section className='space-y-2'>
          <h4 className='text-sm font-medium'>Editor</h4>
          <div className='space-y-2'>
            <div className='space-y-1'>
              <label className='text-xs text-muted-foreground'>Font Size</label>
              <input
                type='number'
                min={10}
                max={48}
                className='w-28 rounded-md border border-input bg-background px-3 py-2 text-sm outline-none focus-visible:ring-1 focus-visible:ring-ring'
                value={editorFont}
                onChange={e => setEditorFont(Number(e.target.value))}
              />
            </div>
            <div className='space-y-1'>
              <label className='text-xs text-muted-foreground'>Theme</label>
              <select
                className='w-full rounded-md border border-input bg-background px-3 py-2 text-sm outline-none focus-visible:ring-1 focus-visible:ring-ring'
                value={editorThemeLocal}
                onChange={e => setEditorThemeLocal(e.target.value as any)}
              >
                <option value='vs'>Light (vs)</option>
                <option value='vs-dark'>Dark (vs-dark)</option>
                <option value='hc-black'>High Contrast (hc-black)</option>
              </select>
            </div>
            <div className='space-y-1'>
              <label className='flex items-center gap-2 text-xs text-muted-foreground'>
                <input
                  type='checkbox'
                  checked={formatOnSaveLocal}
                  onChange={e => setFormatOnSaveLocal(e.target.checked)}
                  className='h-4 w-4 rounded border border-input bg-background focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50'
                />
                Format on Save
              </label>
            </div>
            <div className='flex justify-end'>
              <Button
                onClick={async () => {
                  setStatus('');
                  onEditorPrefsChanged &&
                    onEditorPrefsChanged({
                      fontSize: editorFont,
                      theme: editorThemeLocal,
                      formatOnSave: formatOnSaveLocal,
                    });
                  try {
                    setWorking(true);
                    const res = await fetch('/api/auth/update-editor-prefs', {
                      method: 'POST',
                      headers: {
                        'Content-Type': 'application/json',
                        Authorization: 'Bearer ' + sessionId,
                      },
                      body: JSON.stringify({
                        fontSize: editorFont,
                        editorTheme: editorThemeLocal,
                        formatOnSave: formatOnSaveLocal,
                      }),
                    });
                    let data: any = {};
                    try {
                      if (
                        (res.headers.get('content-type') || '').includes(
                          'application/json'
                        )
                      ) {
                        data = await res.json();
                      } else {
                        await res.text();
                      }
                    } catch {}
                    if (!res.ok)
                      throw new Error(
                        data?.error || 'Failed to update editor preferences'
                      );
                  } catch {
                    setStatus(
                      'Failed to save editor preferences on server — applied locally.'
                    );
                  } finally {
                    setWorking(false);
                  }
                }}
                disabled={working}
              >
                Save Editor Preferences
              </Button>
            </div>
          </div>
        </section>

        <section className='space-y-2'>
          <h4 className='text-sm font-medium'>Change Email</h4>
          <div className='space-y-1'>
            <label className='text-xs text-muted-foreground'>Email</label>
            <input
              type='email'
              className='w-full rounded-md border border-input bg-background px-3 py-2 text-sm outline-none focus-visible:ring-1 focus-visible:ring-ring'
              placeholder='you@example.com'
              value={email}
              onChange={e => setEmail(e.target.value)}
            />
          </div>
          <div className='flex justify-end'>
            <Button onClick={updateEmail} disabled={working}>
              Update Email
            </Button>
          </div>
        </section>

        <section className='space-y-2'>
          <h4 className='text-sm font-medium'>Change Password</h4>
          <div className='space-y-1'>
            <label className='text-xs text-muted-foreground'>
              New Password
            </label>
            <input
              type='password'
              className='w-full rounded-md border border-input bg-background px-3 py-2 text-sm outline-none focus-visible:ring-1 focus-visible:ring-ring'
              value={password}
              onChange={e => setPassword(e.target.value)}
            />
          </div>
          <div className='space-y-1'>
            <label className='text-xs text-muted-foreground'>
              Confirm Password
            </label>
            <input
              type='password'
              className='w-full rounded-md border border-input bg-background px-3 py-2 text-sm outline-none focus-visible:ring-1 focus-visible:ring-ring'
              value={confirmPassword}
              onChange={e => setConfirmPassword(e.target.value)}
            />
          </div>
          <div className='flex justify-end'>
            <Button onClick={updatePassword} disabled={working}>
              Update Password
            </Button>
          </div>
        </section>

        <section className='space-y-2'>
          <h4 className='text-sm font-medium'>Session</h4>
          <Button
            variant='secondary'
            onClick={logout}
            disabled={working}
            className='w-full'
          >
            Logout
          </Button>
        </section>

        <div className='pt-2 text-xs text-muted-foreground'>
          More settings coming soon…
        </div>
      </div>
    </Sheet>
  );
};

export default SettingsPanel;
